import { WHATSAPP_NUMBER } from "@/lib/whatsapp";
import PolicyPage from "@/components/PolicyPage";

const Privacy = () => (
  <PolicyPage
    testId="privacy-page"
    overline="Legal"
    title="Privacy Policy"
    updated="February 16, 2026"
    description="How Luxsova collects, uses and protects your personal data — plus the choices and rights you have as a customer."
  >
    <p>
      This Privacy Policy explains what personal data Luxsova collects when you use{" "}
      <a href="https://luxsova.in">luxsova.in</a>, our WhatsApp concierge or any of our
      services, how we use it, who we share it with, and the rights you have over that data.
      Luxsova is a brand operated by <strong>Apex Enterprises</strong>, Dhamankar Naka Flyover,
      Gala No. 17, Arihant Darshan Building, Bhiwandi, Mumbai, Maharashtra 421302, India.
    </p>

    <h2>1. What we collect</h2>
    <h3>Provided by you</h3>
    <ul>
      <li>Name, email, phone number, shipping and billing addresses.</li>
      <li>Order history, wishlist, reviews, ratings, media (photos/videos) you upload with a review, and messages you send us on chat/WhatsApp.</li>
      <li>Payment identifiers required for refunds (e.g. UPI VPA, last 4 of card, bank IFSC). We do NOT store full card numbers, CVVs or OTPs.</li>
    </ul>
    <h3>Collected automatically</h3>
    <ul>
      <li>Device, browser and OS information, approximate IP-based location, and pages viewed — used to detect fraud, measure performance, and personalise product recommendations.</li>
      <li>Cookies and similar technologies (see &sect;5 below).</li>
    </ul>
    <h3>From third parties</h3>
    <ul>
      <li>Payment status confirmations from our payment processor.</li>
      <li>Delivery tracking events from our courier partners.</li>
      <li>Sign-in identity (name + email) from Google if you choose social login.</li>
    </ul>

    <h2>2. How we use your data</h2>
    <ul>
      <li>Process orders, arrange delivery, handle returns/refunds and provide customer care.</li>
      <li>Send transactional messages (order confirmations, shipment updates, refund receipts) via email, SMS or WhatsApp.</li>
      <li>Personalise the site — for example, showing you recently viewed products or recommending similar pieces.</li>
      <li>Detect and prevent fraud, chargeback abuse and account takeover attempts.</li>
      <li>Comply with tax, accounting and other legal obligations.</li>
      <li>Send marketing offers ONLY if you have opted in — you can opt out at any time from the footer of any email or by replying STOP on WhatsApp.</li>
    </ul>

    <h2>3. Who we share it with</h2>
    <p>We do not sell your data. We share the minimum necessary with:</p>
    <ul>
      <li><strong>Payment processors</strong> (PCI-compliant gateways) to authorise and settle payments.</li>
      <li><strong>Courier partners</strong> to pick up, ship and deliver your order.</li>
      <li><strong>Cloud infrastructure</strong> providers that host the site and store data — under contractual data-protection safeguards.</li>
      <li><strong>Analytics &amp; email tools</strong> that help us understand aggregate usage and send transactional messages.</li>
      <li>
        <strong>Advertising partner — Meta (Facebook / Instagram):</strong>{" "}
        we share event data (page views, product views, add-to-cart,
        checkout and purchases) with Meta so we can measure the performance
        of ads we run and show you relevant Luxsova offers. Your name,
        email, phone and address are only shared with Meta (SHA-256 hashed,
        per Meta&apos;s Conversions API specification) if you tap
        &ldquo;OK&rdquo; on the cookie banner. You can opt out at any
        time from the cookie banner or by clearing site data — see
        &sect;5 below.
      </li>
      <li><strong>Law enforcement or regulators</strong>, if legally required.</li>
    </ul>

    <h2>4. How long we keep it</h2>
    <ul>
      <li>Order records: 7 years, to comply with Indian tax and accounting rules.</li>
      <li>Account data (profile, wishlist, addresses): as long as your account is active. You may delete your account at any time.</li>
      <li>Marketing preferences: until you opt out.</li>
      <li>Chat / WhatsApp logs: 24 months for quality and dispute resolution, then purged.</li>
    </ul>

    <h2>5. Cookies &amp; ad tracking</h2>
    <p>
      We use a small set of cookies and localStorage entries to keep the site
      working smoothly. You can accept or reject the non-essential ones from
      the banner shown on your first visit; your choice is saved on this
      device and can be changed later by clearing site data.
    </p>
    <ul>
      <li>
        <strong>Essential</strong> — login session, cart contents, CSRF
        tokens, consent preference. Cannot be disabled.
      </li>
      <li>
        <strong>Functional</strong> — recently viewed products, saved filter
        preferences (stored in your browser).
      </li>
      <li>
        <strong>Analytics</strong> — anonymised page-view counts (Google
        Analytics 4) so we can improve the site.
      </li>
      <li>
        <strong>Advertising (Meta Pixel)</strong> —
        the Meta Pixel runs by default: it drops small cookies (<code>_fbp</code>,{" "}
        <code>_fbc</code>) and mirrors your visit (page views, product
        views, add-to-cart, checkout and purchase events) to Meta so we can
        measure ad performance and show you Luxsova offers on Facebook and
        Instagram. It does not send your name, email or phone unless you
        tap &ldquo;OK&rdquo; on the cookie banner. If you tap
        &ldquo;Opt out&rdquo;, no advertising cookies or events are sent.
      </li>
    </ul>
    <p>
      Meta processes this data as an independent controller under its own{" "}
      <a
        href="https://www.facebook.com/policy.php"
        target="_blank"
        rel="noreferrer"
      >
        Data Policy
      </a>
      . You can opt out of Meta&apos;s off-Facebook activity from your{" "}
      <a
        href="https://www.facebook.com/off_facebook_activity/"
        target="_blank"
        rel="noreferrer"
      >
        Facebook account settings
      </a>
      .
    </p>

    <h2>6. Your rights</h2>
    <p>Regardless of where you live, you can ask us to:</p>
    <ul>
      <li>Show you the data we hold about you.</li>
      <li>Correct anything inaccurate.</li>
      <li>Delete your account and associated data (subject to legal retention like tax records).</li>
      <li>Stop marketing to you.</li>
      <li>Port your data to another service.</li>
    </ul>
    <p>
      Write to <a href="mailto:care@luxsova.com">care@luxsova.com</a> from the email
      address on file and we will action requests within 30 days.
    </p>

    <h2>7. Security</h2>
    <p>
      We use TLS/HTTPS across the site, hashed passwords (bcrypt), rotated secrets, encrypted
      cloud storage and least-privilege access controls. No system is 100% secure — if you ever
      suspect unauthorised access, please contact us immediately.
    </p>

    <h2>8. Children</h2>
    <p>
      Luxsova is not directed at children under 13, and we do not knowingly collect data from
      them. If you believe a minor has provided us with personal data, please contact us and we
      will delete it.
    </p>

    <h2>9. Changes to this policy</h2>
    <p>
      We may update this policy from time to time. When we do, we&apos;ll change the &ldquo;Last
      updated&rdquo; date at the top, and material changes will be announced via email or a site
      banner.
    </p>

    <h2>10. Contact</h2>
    <p>
      For any privacy question, reach us at{" "}
      <a href="mailto:care@luxsova.com">care@luxsova.com</a> or on{" "}
      <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer">WhatsApp</a>.
    </p>
  </PolicyPage>
);

export default Privacy;
